import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { useSidebar } from '../context/SidebarContext';
import { db } from '../config/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

function EmployeeDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isMobile, isOpen, toggleMobileSidebar } = useSidebar();
  const [employee, setEmployee] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    const fetchEmployee = async () => {
      try {
        const snap = await getDoc(doc(db, 'employees', id));
        if (snap.exists()) {
          const data = { id: snap.id, ...snap.data() };
          setEmployee(data);
          setForm({ name: data.name || '', phone: data.phone || '', department: data.department || '', location: data.location || '', employeeId: data.employeeId || '' });
        }
      } catch (err) { console.error('Error fetching employee:', err); }
      finally { setLoading(false); }
    };
    if (id) fetchEmployee();
  }, [id]);

  const handleToggleStatus = async () => {
    const newStatus = employee.status === 'active' ? 'inactive' : 'active';
    try {
      await updateDoc(doc(db, 'employees', id), { status: newStatus, isActive: newStatus === 'active' });
      setEmployee(prev => ({ ...prev, status: newStatus, isActive: newStatus === 'active' }));
    } catch (err) { console.error('Error updating:', err); }
  };

  const handleSave = async () => {
    try {
      await updateDoc(doc(db, 'employees', id), {
        name: form.name.trim(), phone: form.phone.trim(),
        department: form.department.trim(), location: form.location.trim()
      });
      setEmployee(prev => ({ ...prev, ...form }));
      setEditing(false);
    } catch (err) { console.error('Save failed:', err); }
  };

  if (loading) return (
    <div className="flex h-screen overflow-hidden"><Sidebar /><div className="flex-1 flex items-center justify-center" style={{ marginLeft: isOpen ? '16rem' : '5rem' }}><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-golden"></div></div></div>
  );
  if (!employee) return (
    <div className="flex h-screen overflow-hidden"><Sidebar /><div className="flex-1 flex items-center justify-center" style={{ marginLeft: isOpen ? '16rem' : '5rem' }}><div className="text-center"><p className="text-gray-500 text-lg">Employee not found</p><button onClick={() => navigate('/admin/employees')} className="mt-4 text-brand-golden hover:underline">Back to Employees</button></div></div></div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar />
      <div className="flex-1 overflow-y-auto transition-all duration-300" style={{ marginLeft: isMobile ? 0 : (isOpen ? '16rem' : '5rem') }}>
        <div className="p-8 max-w-4xl">
          <div className="flex items-center mb-6">
            {isMobile && (
              <button onClick={toggleMobileSidebar} className="mr-4 p-2 rounded-lg bg-white border border-gray-200 text-gray-600 hover:text-brand-golden"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" /></svg></button>
            )}
            <button onClick={() => navigate('/admin/employees')} className="mr-4 text-gray-400 hover:text-brand-golden"><svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" /></svg></button>
            <h1 className="text-3xl font-bold text-gray-800">Employee Details</h1>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-8">
              <div className="flex items-center gap-4">
                <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center text-3xl font-bold text-white">{employee.name?.charAt(0)}</div>
                <div>
                  <h2 className="text-2xl font-bold text-white">{employee.name}</h2>
                  <p className="text-white/70">{employee.role?.toUpperCase()} • {employee.department}</p>
                  <span className={`inline-block mt-2 px-3 py-1 rounded-full text-xs font-medium ${employee.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{employee.status?.toUpperCase()}</span>
                </div>
              </div>
            </div>

            <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div><label className="text-xs font-semibold text-gray-400 uppercase">Email</label><p className="text-gray-400 mt-1">{employee.email}</p></div>
              <div><label className="text-xs font-semibold text-gray-400 uppercase">Employee ID</label><p className="text-gray-400 mt-1">{employee.employeeId}</p></div>
              <div>
                <label className="text-xs font-semibold text-gray-400 uppercase">Phone</label>
                {editing ? <input type="tel" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full px-3 py-1 border border-gray-300 rounded mt-1" /> : <p className="text-gray-900 mt-1">{employee.phone}</p>}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-400 uppercase">Department</label>
                {editing ? <input type="text" value={form.department} onChange={e => setForm({...form, department: e.target.value})} className="w-full px-3 py-1 border border-gray-300 rounded mt-1" /> : <p className="text-gray-900 mt-1">{employee.department}</p>}
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-400 uppercase">Location</label>
                {editing ? <input type="text" value={form.location} onChange={e => setForm({...form, location: e.target.value})} className="w-full px-3 py-1 border border-gray-300 rounded mt-1" /> : <p className="text-gray-900 mt-1">{employee.location}</p>}
              </div>
            </div>

            <div className="px-8 pb-8 flex gap-3">
              {editing ? (
                <>
                  <button onClick={handleSave} className="px-6 py-2 bg-brand-golden text-white rounded-lg hover:bg-yellow-600">Save</button>
                  <button onClick={() => setEditing(false)} className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg">Cancel</button>
                </>
              ) : (
                <>
                  <button onClick={() => setEditing(true)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">Edit</button>
                  <button onClick={handleToggleStatus} className={`px-6 py-2 text-white rounded-lg ${employee.status === 'active' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>{employee.status === 'active' ? 'Deactivate' : 'Activate'}</button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeDetails;
